import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Layers, Power } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  const selectedTenantId = localStorage.getItem('selectedTenantId');
  const h = { Authorization: `Bearer ${token}`, ...extra };
  if (selectedTenantId) h['X-Tenant-Id'] = selectedTenantId;
  return h;
};

export default function SetoresOperacao() {
  const [setores, setSetores] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filtroLocal, setFiltroLocal] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ nome: '', descricao: '', local_operacao_id: '' });

  const fetchSetores = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtroLocal) params.set('local_operacao_id', filtroLocal);
      if (filtroStatus) params.set('status', filtroStatus);
      const r = await fetch(`${API_URL}/api/setores-operacao?${params.toString()}`, { headers: authHeaders() });
      if (r.ok) setSetores(await r.json());
    } finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    const r = await fetch(`${API_URL}/api/locations`, { headers: authHeaders() });
    if (r.ok) {
      const data = await r.json();
      setLocations(data.filter((l) => l.status === 'active'));
    }
  };

  useEffect(() => { fetchLocations(); }, []);
  useEffect(() => { fetchSetores(); /* eslint-disable-next-line */ }, [filtroLocal, filtroStatus]);

  const openCreate = () => {
    setEditing(null);
    setForm({ nome: '', descricao: '', local_operacao_id: filtroLocal || '' });
    setShowModal(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ nome: s.nome, descricao: s.descricao || '', local_operacao_id: s.local_operacao_id });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.nome.trim()) { toast.error('Informe o nome do setor'); return; }
    if (!form.local_operacao_id) { toast.error('Selecione o Local de Operação'); return; }
    setSaving(true);
    try {
      const url = editing
        ? `${API_URL}/api/setores-operacao/${editing.id}`
        : `${API_URL}/api/setores-operacao`;
      const r = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ nome: form.nome, descricao: form.descricao || null, local_operacao_id: form.local_operacao_id }),
      });
      if (r.ok) {
        toast.success(editing ? 'Setor atualizado!' : 'Setor cadastrado!');
        setShowModal(false);
        fetchSetores();
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Erro ao salvar setor');
      }
    } finally { setSaving(false); }
  };

  const toggleStatus = async (s) => {
    const novo = s.status === 'ativo' ? 'inativo' : 'ativo';
    const r = await fetch(`${API_URL}/api/setores-operacao/${s.id}`, {
      method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status: novo }),
    });
    if (r.ok) { toast.success(`Setor ${novo === 'ativo' ? 'ativado' : 'inativado'}`); fetchSetores(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Erro'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Excluir o setor "${s.nome}"? Setores com O.S. vinculadas não podem ser excluídos (use inativar).`)) return;
    const r = await fetch(`${API_URL}/api/setores-operacao/${s.id}`, { method: 'DELETE', headers: authHeaders() });
    if (r.ok) { toast.success('Setor excluído'); fetchSetores(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Erro ao excluir'); }
  };

  return (
    <div className="space-y-6" data-testid="setores-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Layers className="text-yellow-500" size={24} /> Setores de Operação
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Segmente atividades e custos dentro de cada Local de Operação. O.S. Operacionais em locais
            com setores cadastrados exigirão a seleção do setor.
          </p>
        </div>
        <button onClick={openCreate} data-testid="setor-add-btn"
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors">
          <Plus size={16} /> Novo Setor
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={filtroLocal} onChange={(e) => setFiltroLocal(e.target.value)}
          data-testid="setor-filtro-local"
          className="px-3 py-2 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white text-sm">
          <option value="">Todos os Locais</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
        </select>
        <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}
          data-testid="setor-filtro-status"
          className="px-3 py-2 bg-[#1A1A1A] border border-gray-800 rounded-lg text-white text-sm">
          <option value="">Todos os status</option>
          <option value="ativo">Ativos</option>
          <option value="inativo">Inativos</option>
        </select>
      </div>

      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-gray-400 uppercase text-xs">
              <tr>
                <th className="px-4 py-3 text-left">Setor</th>
                <th className="px-4 py-3 text-left">Local de Operação</th>
                <th className="px-4 py-3 text-left">Descrição</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Carregando...</td></tr>
              ) : setores.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500" data-testid="setores-vazio">
                  Nenhum setor cadastrado{filtroLocal ? ' para este local' : ''}.
                </td></tr>
              ) : setores.map((s) => (
                <tr key={s.id} className="border-t border-gray-800 hover:bg-white/[0.02]" data-testid={`setor-row-${s.id}`}>
                  <td className="px-4 py-3 text-white font-medium">{s.nome}</td>
                  <td className="px-4 py-3 text-gray-300">{s.local_operacao_nome || s.local_operacao_id}</td>
                  <td className="px-4 py-3 text-gray-400">{s.descricao || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${s.status === 'ativo'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                      : 'bg-gray-500/10 text-gray-400 border border-gray-600'}`}>
                      {s.status}
                    </span>
                    {s.orfao_de_local && (
                      <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/30"
                        title="O Local de Operação deste setor foi inativado/excluído">órfão</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(s)} data-testid={`setor-edit-${s.id}`}
                        className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded" title="Editar">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleStatus(s)} data-testid={`setor-toggle-${s.id}`}
                        className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded"
                        title={s.status === 'ativo' ? 'Inativar' : 'Ativar'}>
                        <Power size={14} />
                      </button>
                      <button onClick={() => remove(s)} data-testid={`setor-delete-${s.id}`}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded" title="Excluir (soft-delete)">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-md w-full" data-testid="setor-modal">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-white">{editing ? 'Editar Setor' : 'Novo Setor de Operação'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white" data-testid="setor-modal-close">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Local de Operação *</label>
                <select value={form.local_operacao_id}
                  onChange={(e) => setForm({ ...form, local_operacao_id: e.target.value })}
                  data-testid="setor-local-select"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none">
                  <option value="">Selecione o local</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Nome do Setor *</label>
                <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  data-testid="setor-nome-input" placeholder="Ex.: Frente Norte, Britagem, Pátio 2..."
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Descrição</label>
                <textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  data-testid="setor-descricao-input" rows={2}
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-800 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="setor-save-btn"
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-semibold rounded-lg text-sm">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
